import {
  Appointment01Icon,
  Briefcase03Icon,
  CalendarCheckOut01Icon,
  Cancel01Icon,
  FileSyncIcon,
  InformationCircleIcon,
  PrisonerIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import moment from "moment";
import { incomeTypeOptions } from "shared/app/InviteAndCreateModal/slices/ManualApplication/slices/CombinedIncome/constants";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  IconButton,
  IconButtonSize
} from "shared/components";
import { IncomeType } from "shared/types";
import { cx, formatToCurrency } from "shared/utils";

export const IntroView = ({
  title,
  description,
  applicantFullName,
  applicantRole,
  incomeSource,
  incomeName,
  incomeStartDate,
  incomeEndDate,
  onClose,
  isLoading,
  cents
}: {
  title: string;
  description: string;
  applicantFullName: string;
  applicantRole: string;
  incomeSource: IncomeType;
  // job title, company
  incomeName: string;
  incomeStartDate: string;
  incomeEndDate: string;
  onClose?: () => void;
  isLoading: boolean;
  cents: number;
}) => {
  const mappedIncomeType =
    incomeTypeOptions.find(({ type }) => type === incomeSource)?.label ||
    incomeSource;

  return (
    <div className="p-6 relative">
      <IconButton
        HugeIcon={Cancel01Icon}
        size={IconButtonSize.SM}
        borderLess
        className="absolute top-4 right-4"
        onClick={onClose}
      />

      <div className="bg-primary-20 h-20 w-20 rounded-full flex items-center justify-center mx-auto mb-6">
        <HugeiconsIcon
          icon={FileSyncIcon}
          size={32}
          className="text-primary-500"
        />
      </div>

      <h1 className="text-xl text-content-1000 font-bold mb-2 text-center">
        {title}
      </h1>
      <p className="pb-6 border-b border-alpha-40 text-center text-sm font-medium text-content-300">
        {description}
      </p>

      <div className="mt-6 pb-[18px] mb-6 border-b border-alpha-40 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-content-400">
          <HugeiconsIcon icon={PrisonerIcon} size={20} className="shrink-0" />
          <p>Person</p>
          <p className="ml-auto">{`${applicantFullName}, ${applicantRole}`}</p>
        </div>
        <div className="flex items-center gap-2 text-sm font-medium text-content-400">
          <HugeiconsIcon
            icon={Briefcase03Icon}
            size={20}
            className="shrink-0"
          />
          <p>Income source</p>
          <p className="ml-auto">{`${mappedIncomeType}, ${incomeName}`}</p>
        </div>

        <div className="flex items-center gap-2 text-sm font-medium text-content-400">
          <HugeiconsIcon
            icon={CalendarCheckOut01Icon}
            size={20}
            className="shrink-0"
          />
          <p>Start date</p>
          <p className="ml-auto">{moment(incomeStartDate).format("ll")}</p>
        </div>

        <div className="flex items-center gap-2 text-sm font-medium text-content-400">
          <HugeiconsIcon
            icon={Appointment01Icon}
            size={20}
            className="shrink-0"
          />
          <p>End date</p>
          <p className="ml-auto">
            {moment(incomeEndDate).isValid()
              ? moment(incomeEndDate).format("ll")
              : "Present"}
          </p>
        </div>
      </div>

      <div className="flex justify-between text-base font-bold mb-6">
        <p className="text-content-1000">Verification cost</p>

        <Badge
          className={cx(
            "!font-bold px-[10px] py-[2px]",
            isLoading
              ? "text-alpha-100 animate-pulse h-[14px] w-10 rounded-lg"
              : "text-content-700"
          )}
          size={BadgeSize.SM}
          variant={BadgeVariant.BASE}
        >
          <span className="text-sm">
            {formatToCurrency(
              {
                cents,
                currency: "USD"
              },
              {
                minimumFractionDigits: 2
              }
            )}
          </span>
        </Badge>
      </div>

      <div className="bg-warning-50 text-warning-700 px-6 py-4 rounded-lg border-alpha-100 flex gap-4 items-center">
        <HugeiconsIcon
          icon={InformationCircleIcon}
          size={20}
          className="shrink-0"
        />

        <p className="text-sm font-medium">
          Re-verifying this income source will replace the original verification
          and you will be charged an additional fee
        </p>
      </div>
    </div>
  );
};
